import { useState } from 'react';
import { Download, ExternalLink, Printer } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { Drawer } from '@/shared/components/ui/Drawer';
import { Button } from '@/shared/components/ui/Button';
import { Badge } from '@/shared/components/ui/Badge';
import { PrintableDocument } from './PrintableDocument';
import { canPreview, detectFileType, deriveFileName, isImage, isPdf, resolveSalesDocUrl } from '../constants';
import { downloadSalesDocPdf } from '../utils/downloadPdf';
import type { SalesDoc } from '../types';

export interface SalesDocDrawerProps {
  doc: SalesDoc | null;
  canManage: boolean;
  canRecordPayment?: boolean;
  deleting?: boolean;
  isPage?: boolean;
  onClose: () => void;
  onEdit: (doc: SalesDoc) => void;
  onDelete: (doc: SalesDoc) => void;
  onRecordPayment?: (doc: SalesDoc) => void;
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  if (value === undefined || value === null || value === '') return null;
  return (
    <div className="flex justify-between gap-4 border-b border-outline py-2 last:border-0">
      <span className="text-body-sm text-on-surface-variant">{label}</span>
      <span className="text-right text-data-md text-on-surface">{value}</span>
    </div>
  );
}

function fmtDate(iso?: string) {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleString('en-IN', { year: 'numeric', month: 'short', day: 'numeric' });
}

export function SalesDocDrawerContent({ doc, canManage, canRecordPayment, deleting, isPage, onEdit, onDelete, onRecordPayment }: SalesDocDrawerProps) {
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  if (!doc) return null;
  const fileUrl = resolveSalesDocUrl(doc.url);
  const ft = detectFileType(fileUrl || doc.url);
  const Icon = ft.icon;

  const handlePrintDocument = async () => {
    try {
      setDownloadingPdf(true);
      await downloadSalesDocPdf(doc);
      toast.success('PDF downloaded successfully.');
    } catch (err) {
      console.error('Failed to generate PDF:', err);
      toast.error('Failed to generate PDF, opening print preview instead.');
      window.print();
    } finally {
      setDownloadingPdf(false);
    }
  };

  const isPdfFile = isPdf(fileUrl);
  const isImageFile = isImage(fileUrl);

  return (
    <div className={`flex flex-col gap-5 ${isPage ? 'max-w-4xl' : ''}`}>
      <div className="flex flex-wrap items-center gap-2">
        <span className={`flex h-9 w-9 items-center justify-center rounded-full bg-surface-variant ${ft.tone === 'danger' ? 'text-danger' : 'text-on-surface-variant'}`}>
          <Icon className="h-5 w-5" />
        </span>
        <Badge tone={ft.tone}>{ft.label}</Badge>
      </div>

      {doc.description && <p className="whitespace-pre-wrap text-data-md text-on-surface">{doc.description}</p>}

      <div className="rounded-2xl border border-outline/70 bg-surface-bright p-5 shadow-surface-1">
        <Row label="File" value={deriveFileName(doc.url) || doc.title || '—'} />
        <Row label="Uploaded By" value={doc.creator_name} />
        <Row label="Uploaded" value={fmtDate(doc.created_at)} />
        {doc.reference_number && <Row label="Reference No." value={doc.reference_number} />}
      </div>

      {/* Document Preview - Show the actual uploaded file directly */}
      <div className={`w-full overflow-hidden border border-outline/70 rounded-2xl bg-surface overflow-y-auto ${isPage ? '' : 'max-h-[550px]'} shadow-sm`}>
        {isPdfFile ? (
          <iframe
            title={doc.title}
            src={fileUrl}
            className="w-full h-[550px] border-0"
            style={{ minHeight: isPage ? '70vh' : '550px' }}
          />
        ) : isImageFile ? (
          <a href={fileUrl} target="_blank" rel="noreferrer">
            <img src={fileUrl} alt={doc.title} className="w-full h-auto max-h-[70vh] object-contain p-4" />
          </a>
        ) : (
          <div className="flex flex-col items-center justify-center p-8 text-center">
            <div className="flex items-center justify-center w-20 h-20 rounded-full bg-primary/10 text-primary mb-4">
              <Icon className="h-10 w-10" />
            </div>
            <h3 className="text-lg font-medium text-on-surface mb-1">{doc.title}</h3>
            <p className="text-on-surface-variant mb-4">{deriveFileName(doc.url)}</p>
            <p className="text-sm text-on-surface-variant mb-6">Preview not available for this file type.</p>
            <div className="flex gap-3">
              <a href={fileUrl} target="_blank" rel="noreferrer">
                <Button variant="outline"><ExternalLink className="h-4 w-4 mr-2" /> Open in New Tab</Button>
              </a>
              <a href={fileUrl} download={deriveFileName(doc.url)}>
                <Button><Download className="h-4 w-4 mr-2" /> Download</Button>
              </a>
            </div>
          </div>
        )}
        
        {/* Hidden printable version for Print/Save PDF button */}
        <div style={{ display: 'none' }}>
          <PrintableDocument doc={doc} />
        </div>
      </div>

      {/* Actions */}
      <div className="flex gap-2">
        <a href={fileUrl} target="_blank" rel="noreferrer" className="flex-1">
          <Button variant="outline" className="w-full"><ExternalLink className="h-4 w-4" /> Open</Button>
        </a>
        <a href={fileUrl} download={deriveFileName(doc.url)}>
          <Button variant="outline"><Download className="h-4 w-4" /> Download</Button>
        </a>
      </div>

      {/* Print Document Button */}
      <div className="flex gap-2">
        <Button
          variant="outline"
          className="w-full flex items-center justify-center gap-2"
          loading={downloadingPdf}
          onClick={handlePrintDocument}
        >
          <Printer className="h-4 w-4" />
          <span>Print / Save as PDF</span>
        </Button>
      </div>

      {canRecordPayment && onRecordPayment && (
        <div className="flex gap-2 border-t border-outline/70 pt-3">
          <Button onClick={() => onRecordPayment(doc)} className="w-full bg-success text-white hover:bg-success/90">
            Record Payment
          </Button>
        </div>
      )}

      {canManage && (
        <div className="flex gap-2 border-t border-outline/70 pt-3">
          <Button onClick={() => onEdit(doc)} className="flex-1">Edit Document</Button>
          <Button variant="danger" loading={deleting} onClick={() => onDelete(doc)}>Delete</Button>
        </div>
      )}
    </div>
  );
}

export function SalesDocDrawer(props: SalesDocDrawerProps) {
  const { doc, onClose } = props;
  return (
    <Drawer open={!!doc} onClose={onClose} title={doc?.title ?? 'Document'}>
      <SalesDocDrawerContent {...props} />
    </Drawer>
  );
}